import { useState, useEffect } from 'react';
import LoginForm from './LoginForm';
import ParentDashboard from './ParentDashboard';
import StaffDashboard from './StaffDashboard';
import SuperAdminDashboard from './SuperAdminDashboard';
import WebsiteAdminDashboard from './dashboards/WebsiteAdminDashboard';
import AdmissionsAdminDashboard from './dashboards/AdmissionsAdminDashboard';
import PrincipalDashboard from './dashboards/PrincipalDashboard';
import ApprovalDashboard from './dashboards/principal-admin/ApprovalDashboard';

/**
 * LoginGateway Component
 * 
 * Handles unified login page and routes to appropriate dashboard
 * - Auto-detects user role from authentication
 * - Routes to specific dashboard based on role:
 *   - super_admin: SuperAdminDashboard
 *   - admin: WebsiteAdminDashboard
 *   - approver: ApprovalDashboard (with DashboardLayout for CMS/Approval management)
 *   - admissions: AdmissionsAdminDashboard
 *   - parent: ParentDashboard
 * - Manages session and user state
 */

function LoginGateway({ apiBase, onBackToHome }) {
  const [userRole, setUserRole] = useState(null); // Role-based routing
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check existing session on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    checkExistingSession();
  }, []);

  /**
   * Determine effective role taking into account current URL path and testing role switcher
   */
  function determineEffectiveRole(userObj, currentPath = window.location.pathname) {
    if (!userObj) return null;
    const rawRoles = userObj.role_codes || userObj.roles || userObj.role || [];
    let roleList = [];
    if (typeof rawRoles === 'string') {
      roleList = rawRoles.split(',').map(r => r.trim()).filter(Boolean);
    } else if (Array.isArray(rawRoles)) {
      roleList = rawRoles.map(role => {
        if (typeof role === 'string') return role.trim();
        if (role && typeof role === 'object') return role.role_code || role.code || role.name || '';
        return '';
      }).filter(Boolean);
    }
    const roleStrings = roleList.map(r => r.toLowerCase());
    const email = String(userObj.email || '').toLowerCase();
    const name = String(userObj.full_name || userObj.username || '').toLowerCase();
    const storedRole = typeof localStorage !== 'undefined' ? localStorage.getItem('active_testing_role') : null;
    const isMasterTester = email.includes('tester') || email.includes('test') || name.includes('test') || name.includes('master') ||
      roleStrings.includes('super_admin') || (roleStrings.length >= 2) || Boolean(storedRole);

    const path = (currentPath || '').toLowerCase();

    // 1. Direct path-based routing (highest priority for explicit dashboard URLs)
    if (path.includes('/director-dashboard')) {
      return 'director';
    }
    if (path.includes('/principal-dashboard') || path.includes('/principal_dashboard')) {
      return 'approver';
    }
    if (path.includes('/admissions-dashboard')) {
      return 'admissions';
    }
    if (path.includes('/website-admin-cms')) {
      return 'admin';
    }
    if (path.includes('/administrator')) {
      return 'super_admin';
    }

    // 2. If stored active_testing_role exists from role switcher
    if (storedRole && ['super_admin', 'director', 'admin', 'approver', 'admissions'].includes(storedRole)) {
      return storedRole;
    }

    // 3. Fallback to default getUserRole priority
    return getUserRole(rawRoles);
  }

  /**
   * Determine user role from roles input (array of strings, array of objects, or comma-separated string)
   * Priority: super_admin > admin > website_admin > approver > admissions > editor > parent > staff
   */
  function getUserRole(roles = []) {
    if (!roles) return null;

    let roleList = [];
    if (typeof roles === 'string') {
      roleList = roles.split(',').map(r => r.trim()).filter(Boolean);
    } else if (Array.isArray(roles)) {
      roleList = roles.map(role => {
        if (typeof role === 'string') return role.trim();
        if (role && typeof role === 'object') return role.role_code || role.code || role.name || '';
        return '';
      }).filter(Boolean);
    }

    const roleStrings = roleList.map(r => r.toLowerCase());
    console.log('[LoginGateway] getUserRole - Input roles:', roles);
    console.log('[LoginGateway] getUserRole - Extracted role strings:', roleStrings);

    if (roleStrings.includes('super_admin')) return 'super_admin';
    if (roleStrings.includes('director') || roleStrings.includes('school_director')) return 'director';
    if (roleStrings.includes('admin')) return 'admin';
    if (roleStrings.includes('website_admin')) return 'admin'; // ← Map website_admin to admin
    if (roleStrings.includes('approver') || roleStrings.includes('principal')) return 'approver';
    if (roleStrings.includes('admissions')) return 'admissions';
    if (roleStrings.includes('editor')) return 'editor';
    if (roleStrings.includes('parent')) return 'parent';
    if (roleStrings.includes('staff')) return 'staff';
    
    console.log('[LoginGateway] getUserRole - No matching role found, returning null');
    return null;
  }

  async function checkExistingSession() {
    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      // Try staff/admin session first
      try {
        console.log('[LoginGateway] ═══════════════════════════════════════════');
        console.log('[LoginGateway] Checking existing session...');
        console.log('[LoginGateway] API endpoint:', `${apiBase}/auth/me`);
        console.log('[LoginGateway] Current path:', window.location.pathname);
        console.log('[LoginGateway] Current hostname:', window.location.hostname);
        
        const response = await fetch(`${apiBase}/auth/me`, {
          method: 'GET',
          credentials: 'include',
          headers,
        });

        console.log('[LoginGateway] ───────────────────────────────────────────');
        console.log('[LoginGateway] Session check response status:', response.status);

        if (response.ok) {
          const data = await response.json();
          console.log('[LoginGateway] ✓ Session data received:', data);
          
          const userObj = data.data?.user || (data.data?.id ? data.data : null);
          if (data.ok && userObj) {
            const role = determineEffectiveRole(userObj, window.location.pathname);
            console.log('[LoginGateway] Session valid! Role:', role, 'User:', userObj.full_name || userObj.email);
            
            if (role) {
              setUserRole(role);
              setUser(userObj);
              setLoading(false);
              
              // Store in localStorage as backup
              try {
                localStorage.setItem('userSession', JSON.stringify({
                  user: userObj,
                  role: role,
                  auth_token: savedToken,
                  timestamp: Date.now()
                }));
              } catch (err) {}
              
              // Ensure URL matches the dashboard path if currently on login or root
              const currentPath = window.location.pathname;
              if (currentPath === '/' || currentPath === '' || currentPath === '/login') {
                let dashboardPath = '/';
                if (role === 'super_admin') dashboardPath = '/administrator';
                else if (role === 'director') dashboardPath = '/director-dashboard';
                else if (role === 'admin') dashboardPath = '/website-admin-cms';
                else if (role === 'approver') dashboardPath = '/principal-dashboard';
                else if (role === 'admissions') dashboardPath = '/admissions-dashboard';
                else if (role === 'parent') dashboardPath = '/parent-dashboard';
                else if (role === 'staff') dashboardPath = '/staff-dashboard';
                
                if (dashboardPath !== '/') {
                  window.history.replaceState({}, '', dashboardPath);
                  console.log('[LoginGateway] ✓ URL updated to:', dashboardPath);
                }
              }
              
              return;
            }
          } else {
            console.log('[LoginGateway] Session check failed - response not ok or missing user');
          }
        } else {
          console.log('[LoginGateway] ✗ Session check response not ok:', response.status);
        }
      } catch (err) {
        console.log('[LoginGateway] Auth session check error:', err);
      }

      // Try parent session
      try {
        console.log('[LoginGateway] Checking parent session...');
        const response = await fetch(`${apiBase}/admissions/me`, {
          method: 'GET',
          credentials: 'include',
          headers,
        });

        console.log('[LoginGateway] Parent session check response status:', response.status);

        if (response.ok) {
          const data = await response.json();
          console.log('[LoginGateway] Parent session data:', data);
          
          const userObj = data.data?.user || (data.data?.id ? data.data : null);
          if (data.ok && userObj) {
            console.log('[LoginGateway] Parent session valid!');
            setUserRole('parent');
            setUser(userObj);
            
            try {
              localStorage.setItem('userSession', JSON.stringify({
                user: userObj,
                role: 'parent',
                auth_token: savedToken,
                timestamp: Date.now()
              }));
            } catch (err) {}
            
            const currentPath = window.location.pathname;
            if (currentPath === '/' || currentPath === '' || currentPath === '/login') {
              window.history.replaceState({}, '', '/parent-dashboard');
            }
            setLoading(false);
            return;
          }
        } else {
          console.log('[LoginGateway] Parent session check failed:', response.status);
        }
      } catch (err) {
        console.log('[LoginGateway] Parent session check error:', err);
      }

      // API session check failed - try localStorage as fallback
      console.log('[LoginGateway] ═══════════════════════════════════════════');
      console.log('[LoginGateway] API session not found - checking localStorage...');
      
      try {
        const storedSession = localStorage.getItem('userSession');
        if (storedSession) {
          const sessionData = JSON.parse(storedSession);
          
          const sessionAge = Date.now() - sessionData.timestamp;
          const maxAge = 24 * 60 * 60 * 1000; // 24 hours
          
          if (sessionAge < maxAge && sessionData.user) {
            const role = determineEffectiveRole(sessionData.user, window.location.pathname) || sessionData.role;
            console.log('[LoginGateway] ✓ Valid localStorage session found!');
            console.log('[LoginGateway] User:', sessionData.user.email || sessionData.user.full_name);
            console.log('[LoginGateway] Effective Role:', role);
            
            setUserRole(role);
            setUser(sessionData.user);
            setLoading(false);
            
            // Ensure URL is correct if currently on login or root
            const currentPath = window.location.pathname;
            let expectedPath = '/';
            if (sessionData.role === 'super_admin') expectedPath = '/administrator';
            else if (sessionData.role === 'admin') expectedPath = '/website-admin-cms';
            else if (sessionData.role === 'approver') expectedPath = '/principal-dashboard';
            else if (sessionData.role === 'admissions') expectedPath = '/admissions-dashboard';
            else if (sessionData.role === 'parent') expectedPath = '/parent-dashboard';
            else if (sessionData.role === 'staff') expectedPath = '/staff-dashboard';
            
            if ((currentPath === '/' || currentPath === '' || currentPath === '/login') && expectedPath !== '/') {
              window.history.replaceState({}, '', expectedPath);
              console.log('[LoginGateway] ✓ URL corrected to:', expectedPath);
            }
            
            return;
          } else {
            console.log('[LoginGateway] ✗ localStorage session expired or invalid - clearing');
            localStorage.removeItem('userSession');
          }
        } else {
          console.log('[LoginGateway] No localStorage session found');
        }
      } catch (err) {
        console.error('[LoginGateway] Error reading localStorage:', err);
        localStorage.removeItem('userSession');
      }

      // No session found - user is not authenticated
      console.log('[LoginGateway] ═══════════════════════════════════════════');
      console.log('[LoginGateway] ✗ No valid session found - showing login form');
      console.log('[LoginGateway] ═══════════════════════════════════════════');
      setUserRole(null);
      setUser(null);
    } catch (err) {
      console.error('Error checking session:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleLoginSuccess(result) {
    console.log('[LoginGateway] ✓✓✓ Login successful:', result);
    
    const userObj = result.user;
    const role = result.type === 'parent' ? 'parent' : determineEffectiveRole(userObj, window.location.pathname);
    console.log('[LoginGateway] User role:', role);
    console.log('[LoginGateway] User roles data:', userObj.roles, userObj.role_codes);
    
    setUserRole(role);
    setUser(userObj);
    setLoading(false);
    
    try {
      localStorage.setItem('userSession', JSON.stringify({
        user: userObj,
        role: role,
        auth_token: result.auth_token,
        timestamp: Date.now()
      }));
      if (result.auth_token) {
        localStorage.setItem('ph_auth_token', result.auth_token);
      }
      console.log('[LoginGateway] ✓ Session stored in localStorage with auth_token');
    } catch (err) {
      console.error('[LoginGateway] Failed to store session:', err);
    }
    
    console.log('[LoginGateway] ✓ State set - user:', userObj.email, 'role:', role);
    
    // Update URL to reflect the dashboard path without page reload
    const currentPath = window.location.pathname;
    if (currentPath === '/' || currentPath === '' || currentPath === '/login') {
      let dashboardPath = '/';
      if (role === 'super_admin') dashboardPath = '/administrator';
      else if (role === 'director') dashboardPath = '/director-dashboard';
      else if (role === 'admin') dashboardPath = '/website-admin-cms';
      else if (role === 'approver') dashboardPath = '/principal-dashboard';
      else if (role === 'admissions') dashboardPath = '/admissions-dashboard';
      else if (role === 'parent') dashboardPath = '/parent-dashboard';
      else if (role === 'staff') dashboardPath = '/staff-dashboard';
      
      if (dashboardPath !== '/') {
        window.history.replaceState({}, '', dashboardPath);
        window.dispatchEvent(new PopStateEvent('popstate'));
        console.log('[LoginGateway] ✓ URL updated to:', dashboardPath);
      }
    }
  }

  async function handleLogout() {
    console.log('[LoginGateway] Logout started...');
    
    // Capture token before clearing
    const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token');
    
    // 1. Clear ALL storage items synchronously first
    try {
      localStorage.removeItem('userSession');
      localStorage.removeItem('sessionToken');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('ph_auth_token');
      localStorage.removeItem('phs_session');
      localStorage.removeItem('active_testing_role');
      sessionStorage.clear();
      console.log('[LoginGateway] ✓ All storage data cleared');
    } catch (err) {
      console.error('[LoginGateway] Failed to clear storage:', err);
    }

    // 2. Clear state immediately
    setUserRole(null);
    setUser(null);

    // 3. Expire cookies on client side
    try {
      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date(0).toUTCString() + ";path=/");
      });
    } catch (_) {}

    // 4. Call server logout endpoints with Authorization header
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }
      
      try {
        await fetch(`${apiBase}/auth/logout`, {
          method: 'POST',
          credentials: 'include',
          headers,
        });
      } catch (_) {}

      try {
        await fetch(`${apiBase}/admissions/logout`, {
          method: 'POST',
          credentials: 'include',
          headers,
        });
      } catch (_) {}
    } catch (err) {
      console.error('Logout error:', err);
    }

    setLoading(false);
    console.log('[LoginGateway] ✓ Logout complete - redirecting to /login');
    
    // 5. Redirect cleanly to /login with full page reload
    window.location.href = '/login';
  }

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: '#f5f7fa',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '4px solid #e0e0e0',
              borderTop: '4px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px',
            }}
          />
          <p style={{ color: '#999', fontSize: '14px' }}>Memuat...</p>
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!userRole || !user) {
    return (
      <LoginForm
        onLoginSuccess={handleLoginSuccess}
        apiBase={apiBase}
      />
    );
  }

  // Route to appropriate dashboard based on role
  if (userRole === 'super_admin') {
    return (
      <SuperAdminDashboard
        user={user}
        onLogout={handleLogout}
        apiBase={apiBase}
      />
    );
  }

  if (userRole === 'director') {
    return (
      <ApprovalDashboard
        user={user}
        onLogout={handleLogout}
        apiBase={apiBase}
        userRole="director"
      />
    );
  }

  if (userRole === 'admin') {
    return (
      <WebsiteAdminDashboard
        user={user}
        onLogout={handleLogout}
        apiBase={apiBase}
      />
    );
  }

  if (userRole === 'approver') {
    return (
      <ApprovalDashboard
        user={user}
        onLogout={handleLogout}
        apiBase={apiBase}
        userRole="approver"
      />
    );
  }

  if (userRole === 'admissions') {
    return (
      <AdmissionsAdminDashboard
        user={user}
        onLogout={handleLogout}
        apiBase={apiBase}
      />
    );
  }

  if (userRole === 'parent') {
    return (
      <ParentDashboard
        user={user}
        onLogout={handleLogout}
        apiBase={apiBase}
      />
    );
  }

  // Fallback for editor or other roles
  return (
    <StaffDashboard
      user={user}
      onLogout={handleLogout}
      apiBase={apiBase}
    />
  );
}

export default LoginGateway;
